import { Injectable } from '@angular/core';
import { ApiService, IPagingResponse, PagingApiService } from '@core/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { environment } from '@env';
import { map, tap } from 'rxjs/operators';
import {
  EProfileTypeDTO,
  ICnes,
  IProfileTag,
  IProfileTagCreateData,
  IProfileTagDTO,
  IUnit,
  ProfileTagViewModelFactory,
} from '@project/view-models';
import { TGuid } from '@core/helpers';

@Injectable({
  providedIn: 'root',
})
export class ProfilesTagsApiProviderService {
  private readonly _allGroups$ = new BehaviorSubject<IProfileTag[]>([]);

  constructor(private apiService: ApiService, private pagingApiService: PagingApiService) {}

  getAll(profileType: EProfileTypeDTO): Observable<IProfileTag[]> {
    return this.apiService
      .get<IProfileTagDTO[]>(`${environment.environmentVariables.apiCoreUrl}/profiles/tags?profileType=${profileType}`)
      .pipe(map((tagsDto) => tagsDto.map((dto) => ProfileTagViewModelFactory.createGetViewModel(dto))));
  }

  public getAllGroups(): Observable<IProfileTag[]> {
    return this.apiService.get<IProfileTagDTO[]>(`${environment.environmentVariables.apiCoreUrl}/profiles/tags`).pipe(
      map((dtos) => dtos.map(ProfileTagViewModelFactory.createGetViewModel)),
      tap((groups) => this._allGroups$.next(groups)),
    );
  }

  public getRemoteGroup(): Observable<IProfileTag[]> {
    return this.apiService
      .get<IProfileTagDTO[]>(`${environment.environmentVariables.apiCoreUrl}/profiles/tags/remote`)
      .pipe(map((dtos) => dtos.map(ProfileTagViewModelFactory.createGetViewModel)));
  }

  remove(id: TGuid): Observable<void> {
    return this.apiService.delete(`${environment.environmentVariables.apiCoreUrl}/profiles/tags/${id}`);
  }

  create(data: IProfileTagCreateData): Observable<IProfileTag> {
    const dto = ProfileTagViewModelFactory.createPostDTO(data);
    return this.apiService
      .post<IProfileTagDTO>(`${environment.environmentVariables.apiCoreUrl}/profiles/tags`, dto)
      .pipe(map((resultDto) => ProfileTagViewModelFactory.createGetViewModel(resultDto)));
  }

  getTagsWithoutProfile(params?: { search?: string }): Observable<IProfileTag[]> {
    const queryParams = params?.search ? `?search=${params.search}` : '';
    return this.apiService
      .get<IProfileTagDTO[]>(`${environment.environmentVariables.apiCoreUrl}/profiles/tags${queryParams}`)
      .pipe(map((tagsDto) => tagsDto.map((dto) => ProfileTagViewModelFactory.createGetViewModel(dto))));
  }

  getGroupById(id: TGuid): Observable<IProfileTag> {
    const cached = this._allGroups$.value.find((g) => g.id === id);
    if (cached) {
      return of(cached);
    }

    return this.getAllGroups().pipe(map((groups) => groups.find((g) => g.id === id)));
  }
  update(id: TGuid, data: IProfileTagCreateData): Observable<void> {
    const dto = ProfileTagViewModelFactory.createPostDTO(data);
    return this.apiService.post(`${environment.environmentVariables.apiCoreUrl}/profiles/tags/${id}`, dto).pipe(
      tap(() => {
        this._allGroups$.next([]);
      }),
    );
  }

  getCnes(
    itemsPerPage: number,
    page: number,
    uf: string,
    city?: string,
    search?: string,
  ): Observable<IPagingResponse<ICnes>> {
    const queryParams: Map<string, any> = new Map();
    queryParams.set('siglaestado', uf);
    queryParams.set('municipio', city);
    queryParams.set('filter', search);

    const queryString = this.createQueryString(queryParams);

    return this.pagingApiService.request<ICnes, ICnes>({
      url: `${environment.environmentVariables.apiCoreUrl}/profiles/tags/cnes/paged/${queryString}`,
      additionalQueryParams: queryParams,
      itemsPerPage,
      itemFactory: (item) => item,
      pageNumber: page,
    });
  }

  getUnitsAsIUnit(
    itemsPerPage: number,
    page: number,
    uf: string,
    city?: string,
    search?: string,
  ): Observable<{ totalItemsCount: number; items: IUnit[] }> {
    return this.getCnes(itemsPerPage, page, uf, city, search).pipe(
      map((response) => ({
        totalItemsCount: response.totalItemsCount,
        items: response.items.map((item) => ({
          cnes_code: item.cnes_code,
          nome_estabelecimento: item.nome_estabelecimento,
          group_id: item.tag_id,
        })),
      })),
    );
  }

  getCityCnes(uf: string): Observable<ICnes[]> {
    const params = new Map();
    params.set('siglaestado', uf);

    const queryString = this.createQueryString(params);

    return this.apiService.get(
      `${environment.environmentVariables.apiCoreUrl}/profiles/tags/cnes/municipio/${queryString}`,
    );
  }

  private createQueryString(map: Map<string, any>): string {
    const params = Array.from(map.entries())
      .filter(([key, value]) => value !== undefined && value !== null)
      .map(([key, value]) => encodeURIComponent(key) + '=' + encodeURIComponent(value))
      .join('&');

    return params ? `?${params}` : '';
  }
}
